/**
 * ReaderFunnelContext - Global state for Reader Funnel (Anonymous -> Registered -> Conversion)
 * 
 * Manages:
 * - Anonymous vs Registered user state
 * - Credit balance thresholds (<5, <2, ==0)
 * - Credit prompt cooldowns
 * - Onboarding status
 * - Membership conversion flow
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useReaderCredits } from './ReaderCreditContext';
import { BACKEND_URL as backendUrl } from '@/config/api';

const ReaderFunnelContext = createContext(null);

// Cooldown durations (in hours)
const LOW_CREDIT_COOLDOWN_HOURS = 24;  // <5 RC
const URGENT_CREDIT_COOLDOWN_HOURS = 4;  // <2 RC

export const useReaderFunnel = () => {
  const context = useContext(ReaderFunnelContext);
  if (!context) {
    throw new Error('useReaderFunnel must be used within a ReaderFunnelProvider');
  }
  return context;
};

export const ReaderFunnelProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { balance } = useReaderCredits();
  
  // Credit prompts ONLY apply to pure Readers (not Admins, Authors, Publishers, or Beta Testers)
  // Users with admin/author/publisher roles should NOT see credit prompts even if they also have 'reader' role
  // Beta testers are exempt from credit gates but still accrue usage
  const userRoles = user?.roles || [];
  const hasPrivilegedRole = userRoles.includes('admin') || userRoles.includes('author') || userRoles.includes('publisher') || userRoles.includes('creator');
  const isBetaTester = userRoles.includes('beta_tester');
  const isReaderOnly = userRoles.includes('reader') && !hasPrivilegedRole && !isBetaTester;
  
  // Modal states
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showLowCreditModal, setShowLowCreditModal] = useState(false);
  const [showUrgentCreditModal, setShowUrgentCreditModal] = useState(false);
  const [showCreditExpiredModal, setShowCreditExpiredModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  // Cooldown timestamps (from server)
  const [lastLowCreditPrompt, setLastLowCreditPrompt] = useState(null);
  const [lastUrgentCreditPrompt, setLastUrgentCreditPrompt] = useState(null);
  
  // Gate trigger context
  const [gateContext, setGateContext] = useState(null);  // { action: 'listen', manuscriptId: '...' }
  
  /**
   * Check if user is anonymous (not authenticated)
   */
  const isAnonymous = !isAuthenticated;
  
  /**
   * Get credit state based on balance
   * Returns: 'active' | 'low' | 'urgent' | 'expired'
   */
  const getCreditState = useCallback(() => {
    if (balance === null || balance === undefined) return 'unknown';
    if (balance === 0) return 'expired';
    if (balance < 2) return 'urgent';
    if (balance < 5) return 'low';
    return 'active';
  }, [balance]);
  
  /**
   * Check if cooldown has passed
   */
  const isCooldownPassed = useCallback((lastPromptTime, cooldownHours) => {
    if (!lastPromptTime) return true;
    
    const lastPrompt = new Date(lastPromptTime);
    const now = new Date();
    const hoursSince = (now - lastPrompt) / (1000 * 60 * 60);
    
    return hoursSince >= cooldownHours;
  }, []);
  
  /**
   * Update cooldown on server
   */
  const updateCooldown = useCallback(async (promptType) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      
      // backendUrl imported from centralized config as BACKEND_URL
      const response = await fetch(`${backendUrl}/api/credits/prompt-cooldown`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt_type: promptType })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (promptType === 'low') {
          setLastLowCreditPrompt(data.updated_at);
        } else {
          setLastUrgentCreditPrompt(data.updated_at);
        }
      }
    } catch (error) {
      console.error('[FUNNEL] Failed to update cooldown:', error);
    }
  }, []);
  
  /**
   * Fetch cooldowns from server on mount/auth change
   */
  const fetchCooldowns = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      
      // backendUrl imported from centralized config as BACKEND_URL
      const response = await fetch(`${backendUrl}/api/credits/prompt-cooldowns`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastLowCreditPrompt(data.last_low_credit_prompt_at);
        setLastUrgentCreditPrompt(data.last_urgent_credit_prompt_at);
      }
    } catch (error) {
      console.error('[FUNNEL] Failed to fetch cooldowns:', error);
    }
  }, []);
  
  // Fetch cooldowns when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCooldowns();
    }
  }, [isAuthenticated, fetchCooldowns]);
  
  /**
   * ANONYMOUS GATE: Check if action is allowed for anonymous users
   * 
   * Returns true if action is BLOCKED (should show registration modal)
   */
  const checkAnonymousGate = useCallback((action, manuscriptId = null, manuscriptTitle = null, isPromotional = false) => {
    if (!isAnonymous) return false;  // Registered users pass
    
    // Anonymous users can ONLY:
    // 1. Browse library and view modals
    // 2. Play promotional books (is_promotional=true)
    // 3. Click "Ask Audia" (but get gated on deeper interaction)
    
    if (action === 'listen') {
      // Allow any promotional book for anonymous users
      if (!isPromotional) {
        setGateContext({ action, manuscriptId, manuscriptTitle });
        setShowRegistrationModal(true);
        return true;  // BLOCKED
      }
      return false;  // ALLOWED
    }
    
    if (action === 'ask_audia') {
      // Allow initial click, but gate on deeper interaction
      // For now, gate everything Ask Audia for anonymous
      setGateContext({ action, manuscriptId, manuscriptTitle });
      setShowRegistrationModal(true);
      return true;  // BLOCKED
    }
    
    if (action === 'library_action') {
      // Adding to library, etc. requires registration
      setGateContext({ action, manuscriptId, manuscriptTitle });
      setShowRegistrationModal(true);
      return true;  // BLOCKED
    }
    
    return false;  // ALLOWED by default
  }, [isAnonymous]);
  
  /**
   * CREDIT GATE: Check if action is allowed based on credit balance
   * 
   * Returns true if action is BLOCKED (should show credit modal)
   * 
   * NOTE: Only applies to Readers (not Admins, Authors, Publishers, or Beta Testers)
   * Beta testers can accrue negative balance for usage tracking
   */
  const checkCreditGate = useCallback((action, manuscriptId = null) => {
    // Beta testers bypass all credit gates
    if (isBetaTester) {
      console.log('[FUNNEL] checkCreditGate: Beta tester, bypassing gate');
      return false;
    }
    
    if (isAnonymous || !isReaderOnly) return false;  // Only pure Readers use credits
    
    const creditState = getCreditState();
    
    if (creditState === 'expired') {
      // Hard gate at 0 credits
      setGateContext({ action, manuscriptId });
      setShowCreditExpiredModal(true);
      return true;  // BLOCKED
    }
    
    // For low/urgent, we don't block but may show prompts (handled separately)
    return false;
  }, [isAnonymous, isReaderOnly, isBetaTester, getCreditState]);
  
  /**
   * Check and show low-credit prompts (non-blocking)
   * Call this after successful actions
   * 
   * NOTE: Only applies to Readers (not Admins, Authors, Publishers, or Beta Testers)
   */
  const checkLowCreditPrompts = useCallback(() => {
    // P0 GUARD: Never show credit prompts to anonymous or non-Reader users
    if (isAnonymous) {
      console.log('[FUNNEL] checkLowCreditPrompts: Anonymous user, skipping');
      return;
    }
    // Beta testers don't see credit prompts
    if (isBetaTester) {
      console.log('[FUNNEL] checkLowCreditPrompts: Beta tester, skipping');
      return;
    }
    if (!isReaderOnly) {
      console.log('[FUNNEL] checkLowCreditPrompts: Not a pure Reader, skipping');
      return;
    }
    // Also guard against null/undefined balance (unhydrated state)
    if (balance === null || balance === undefined) {
      console.log('[FUNNEL] checkLowCreditPrompts: Balance not loaded yet, skipping');
      return;
    }
    
    const creditState = getCreditState();
    console.log('[FUNNEL] checkLowCreditPrompts called, creditState:', creditState, 'balance:', balance);
    
    if (creditState === 'urgent') {
      // <2 credits - check urgent cooldown (4 hours)
      if (isCooldownPassed(lastUrgentCreditPrompt, URGENT_CREDIT_COOLDOWN_HOURS)) {
        console.log('[FUNNEL] Showing URGENT credit modal (balance < 2)');
        setShowUrgentCreditModal(true);
        updateCooldown('urgent');
      }
    } else if (creditState === 'low') {
      // <5 credits - check low cooldown (24 hours)
      if (isCooldownPassed(lastLowCreditPrompt, LOW_CREDIT_COOLDOWN_HOURS)) {
        console.log('[FUNNEL] Showing LOW credit modal (balance < 5)');
        setShowLowCreditModal(true);
        updateCooldown('low');
      }
    }
  }, [isAnonymous, isReaderOnly, getCreditState, isCooldownPassed, lastLowCreditPrompt, lastUrgentCreditPrompt, updateCooldown, balance]);
  
  /**
   * AUTO-CHECK: Watch balance changes and trigger low credit prompts
   * This ensures modals appear when balance drops below thresholds
   * 
   * NOTE: Only applies to Readers (not Admins, Authors, or Publishers)
   */
  useEffect(() => {
    // CRITICAL: Only check for authenticated Readers with known balance
    // This must NEVER trigger for anonymous/unauthenticated users
    if (!isAuthenticated) {
      console.log('[FUNNEL] AUTO-CHECK skipped: Not authenticated');
      return;
    }
    if (isAnonymous) {
      console.log('[FUNNEL] AUTO-CHECK skipped: Anonymous user');
      return;
    }
    if (!isReaderOnly) {
      console.log('[FUNNEL] AUTO-CHECK skipped: Not a pure Reader');
      return;
    }
    if (balance === null || balance === undefined) {
      console.log('[FUNNEL] AUTO-CHECK skipped: Balance not loaded');
      return;
    }
    
    console.log('[FUNNEL] Balance changed to:', balance, '- checking thresholds');
    
    // Check thresholds
    if (balance === 0) {
      // Don't auto-show expired modal here - that's triggered by action attempts
      console.log('[FUNNEL] Balance is 0 - expired state');
    } else if (balance < 2) {
      // Urgent threshold
      if (isCooldownPassed(lastUrgentCreditPrompt, URGENT_CREDIT_COOLDOWN_HOURS)) {
        console.log('[FUNNEL] AUTO: Showing URGENT credit modal (balance < 2)');
        setShowUrgentCreditModal(true);
        updateCooldown('urgent');
      }
    } else if (balance < 5) {
      // Low threshold
      if (isCooldownPassed(lastLowCreditPrompt, LOW_CREDIT_COOLDOWN_HOURS)) {
        console.log('[FUNNEL] AUTO: Showing LOW credit modal (balance < 5)');
        setShowLowCreditModal(true);
        updateCooldown('low');
      }
    }
  }, [balance, isAuthenticated, isAnonymous, isReaderOnly, isCooldownPassed, lastLowCreditPrompt, lastUrgentCreditPrompt, updateCooldown]);
  
  /**
   * AUTO-TRIGGER: Show onboarding for Readers with pending or in_progress status
   */
  useEffect(() => {
    const status = user?.onboarding_status;
    if (!isAnonymous && isReaderOnly && (status === 'pending' || status === 'in_progress')) {
      console.log('[FUNNEL] Triggering onboarding for reader with status:', status);
      // Small delay to let the page settle after login
      const timer = setTimeout(() => {
        setShowOnboardingModal(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isAnonymous, isReaderOnly, user?.onboarding_status]);
  
  /**
   * Trigger onboarding modal
   * Call after successful registration
   */
  const triggerOnboarding = useCallback(() => {
    const onboardingStatus = user?.onboarding_status || 'pending';
    if (onboardingStatus === 'pending') {
      setShowOnboardingModal(true);
    }
  }, [user]);
  
  /**
   * Close all modals
   */
  const closeAllModals = useCallback(() => {
    setShowRegistrationModal(false);
    setShowLowCreditModal(false);
    setShowUrgentCreditModal(false);
    setShowCreditExpiredModal(false);
    setShowOnboardingModal(false);
    setShowPricingModal(false);
    setGateContext(null);
  }, []);
  
  const value = {
    // State
    isAnonymous,
    isBetaTester,
    creditState: getCreditState(),
    gateContext,
    
    // Modal states
    showRegistrationModal,
    showLowCreditModal,
    showUrgentCreditModal,
    showCreditExpiredModal,
    showOnboardingModal,
    showPricingModal,
    
    // Modal setters
    setShowRegistrationModal,
    setShowLowCreditModal,
    setShowUrgentCreditModal,
    setShowCreditExpiredModal,
    setShowOnboardingModal,
    setShowPricingModal,
    
    // Gate functions
    checkAnonymousGate,
    checkCreditGate,
    checkLowCreditPrompts,
    
    // Actions
    triggerOnboarding,
    closeAllModals
  };
  
  return (
    <ReaderFunnelContext.Provider value={value}>
      {children}
    </ReaderFunnelContext.Provider>
  );
};

export default ReaderFunnelContext;
